import type { ReactElement } from 'react';
import { Lock, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';

import { useOidcCredentialStatus, type OidcCredentialViewModel } from '../api';
import { OidcCredentialStatusBadge } from './OidcCredentialStatusBadge';

export function OidcCredentialsTable({
  rows,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly rows: readonly OidcCredentialViewModel[];
  readonly onEdit: (row: OidcCredentialViewModel) => void;
  readonly onDelete: (row: OidcCredentialViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.oidcCredentials.table';

  return (
    <DataTableTable aria-label={t('routes.admin.oidcCredentials.heading')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.label`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[9rem]">
            {t(`${col}.type`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0">{t(`${col}.issuer`)}</DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[8rem]">
            {t(`${col}.serverStatus`)}
          </DataTableTh>
          <DataTableTh className="h-[40px] py-0" widthClassName="w-[6rem]">
            {t(`${col}.actions`)}
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {rows.map((row) => (
          <CredentialRow
            key={row.id}
            row={row}
            onEdit={onEdit}
            onDelete={onDelete}
            pendingDeleteId={pendingDeleteId}
          />
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

function CredentialRow({
  row,
  onEdit,
  onDelete,
  pendingDeleteId,
}: {
  readonly row: OidcCredentialViewModel;
  readonly onEdit: (row: OidcCredentialViewModel) => void;
  readonly onDelete: (row: OidcCredentialViewModel) => void;
  readonly pendingDeleteId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const col = 'routes.admin.oidcCredentials.table';
  // The synthetic built-in entry (read-only) is greyed when its bootstrap-IdP probe is down.
  const statusQuery = useOidcCredentialStatus(row.id);
  const down = statusQuery.data?.ok === false;
  const dimmed = row.readOnly && (down || statusQuery.isError);

  return (
    <DataTableTr className={`even:bg-muted/55${dimmed ? ' opacity-50' : ''}`}>
      <DataTableTd className="h-[40px] py-0 text-foreground/90" dataOffset>
        <span className="inline-flex items-center gap-2">
          {row.label}
          {row.isDefault && <Badge variant="default">{t(`${col}.defaultBadge`)}</Badge>}
          {row.readOnly && <Badge variant="outline">{t(`${col}.builtInBadge`)}</Badge>}
        </span>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        <Badge variant="outline">{t(`routes.admin.oidcCredentials.typeLabel.${row.type}`)}</Badge>
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0 font-mono text-xs text-muted-foreground" dataOffset={false}>
        {row.issuerUri}
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        <OidcCredentialStatusBadge credentialId={row.id} />
      </DataTableTd>
      <DataTableTd className="h-[40px] py-0" dataOffset={false}>
        {row.readOnly ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            title={t(`${col}.builtInHint`)}
          >
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            {t(`${col}.builtInBadge`)}
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              aria-label={t(`${col}.editAction`)}
              title={t(`${col}.editAction`)}
              onClick={() => {
                onEdit(row);
              }}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label={t(`${col}.deleteAction`)}
              title={t(`${col}.deleteAction`)}
              loading={pendingDeleteId === row.id}
              onClick={() => {
                onDelete(row);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </DataTableTd>
    </DataTableTr>
  );
}
